import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../../utils/constants";


const initialProfile = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  avatar: null,
  avatarPreview: null,
};
const initialCompany = {
  name: "",
  email: "",
  phone: "",
  address: "",
  gstin: "",
  logo: null,
  logoPreview: null,
};
const initialBank = {
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  branch: "",
  ifsc: "",
  address: "",
  upiId: "",
  upiQr: null,
  upiQrPreview: null,
};
const initialNotifications = {
  overall: true,
  events: true,

  eventRemindersEnabled: true,
  eventReminderLeadMinutes: 120,
  projectRemindersEnabled: true,
  projectReminderLeadMinutes: 120,
  muteNotificationSound: false,
};

const initialColumnPreferences = {
  quotations: null,
  events: null,
  billing: null,
};

const initialAnalyticsLayout = {
  order: [],
};

const initialState = {
  profile: initialProfile,
  company: initialCompany,
  bank: initialBank,
  notifications: initialNotifications,
  columnPreferences: initialColumnPreferences,
  analyticsLayout: initialAnalyticsLayout,
  siteSettings: {
    youtubeUrl: "",
  },
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateProfile: (state, action) => {
      state.profile = { ...state.profile, ...action.payload };
    },
    updateCompany: (state, action) => {
      state.company = { ...state.company, ...action.payload };
    },
    updateBank: (state, action) => {
      state.bank = { ...state.bank, ...action.payload };
    },
    updateNotifications: (state, action) => {
      state.notifications = { ...state.notifications, ...action.payload };
    },
    updateColumnPreferences: (state, action) => {
      state.columnPreferences = {
        ...state.columnPreferences,
        ...action.payload,
      };
    },
    updateAnalyticsLayout: (state, action) => {
      state.analyticsLayout = {
        ...state.analyticsLayout,
        ...action.payload,
      };
    },
    setSettings: (state, action) => {
      state.profile = { ...state.profile, ...action.payload.profile };
      state.company = { ...state.company, ...action.payload.company };
      state.bank = { ...state.bank, ...action.payload.bank };
      state.notifications = {
        ...state.notifications,
        ...action.payload.notifications,
      };
      if (action.payload.columnPreferences) {
        state.columnPreferences = {
          ...state.columnPreferences,
          ...action.payload.columnPreferences,
        };
      }
      if (action.payload.analyticsLayout) {
        state.analyticsLayout = {
          ...state.analyticsLayout,
          ...action.payload.analyticsLayout,
        };
      }
    },
    clearSettings: (state) => {
      state.profile = initialState.profile;
      state.company = initialState.company;
      state.bank = initialState.bank;
      state.notifications = initialState.notifications;
      state.columnPreferences = initialColumnPreferences;
      state.analyticsLayout = initialAnalyticsLayout;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveProfile.fulfilled, (state, action) => {
        state.profile = { ...state.profile, ...action.payload };
      })
      .addCase(saveProfile.rejected, (state, action) => {
        console.error("Failed to save profile:", action.payload);
      })
      .addCase(saveCompany.fulfilled, (state, action) => {
        state.company = { ...state.company, ...action.payload };
      })
      .addCase(saveCompany.rejected, (state, action) => {
        console.error("Failed to save company:", action.payload);
      })
      .addCase(saveBank.fulfilled, (state, action) => {
        state.bank = { ...state.bank, ...action.payload };
      })
      .addCase(saveBank.rejected, (state, action) => {
        console.error("Failed to save bank:", action.payload);
      })
      .addCase(saveNotifications.fulfilled, (state, action) => {
        state.notifications = { ...state.notifications, ...action.payload };
      })
      .addCase(saveNotifications.rejected, (state, action) => {
        console.error("Failed to save notifications:", action.payload);
      })
      .addCase(saveColumnPreferences.fulfilled, (state, action) => {
        if (action.payload) {
          state.columnPreferences = {
            ...state.columnPreferences,
            ...action.payload,
          };
        }
      })
      .addCase(saveColumnPreferences.rejected, (state, action) => {
        console.error("Failed to save column preferences:", action.payload);
      })
      .addCase(saveAnalyticsLayout.fulfilled, (state, action) => {
        if (action.payload) {
          state.analyticsLayout = {
            ...state.analyticsLayout,
            ...action.payload,
          };
        }
      })
      .addCase(saveAnalyticsLayout.rejected, (state, action) => {
        console.error("Failed to save analytics layout:", action.payload);
      })
      .addCase(loadSettings.fulfilled, (state, action) => {
        if (action.payload) {
          // Extract nested objects from response and merge with existing state
          const payload = action.payload;
          const profile = payload.profile || {};
          const company = payload.company || {};
          const bank = payload.bank || {};
          const notifications = payload.notifications || {};

          state.profile = { ...state.profile, ...profile };
          state.company = { ...state.company, ...company };
          state.bank = { ...state.bank, ...bank };
          state.notifications = { ...state.notifications, ...notifications };
          if (payload.columnPreferences) {
            state.columnPreferences = {
              ...state.columnPreferences,
              ...payload.columnPreferences,
            };
          }
          if (payload.analyticsLayout) {
            state.analyticsLayout = {
              ...state.analyticsLayout,
              ...payload.analyticsLayout,
            };
          }
          // Site-level settings (e.g., youtubeUrl, ad settings)
          if (payload.youtubeUrl || payload.siteSettings) {
            state.siteSettings = {
              ...state.siteSettings,
              ...(payload.siteSettings || {}),
              youtubeUrl:
                payload.youtubeUrl ||
                (payload.siteSettings && payload.siteSettings.youtubeUrl) ||
                state.siteSettings.youtubeUrl,
            };
          }
        }
      })
      .addCase(loadSettings.rejected, (state, action) => {
        console.error("Failed to load settings:", action.payload);
      });
  },
});

export const {
  updateProfile,
  updateCompany,
  updateBank,
  updateNotifications,
  updateColumnPreferences,
  updateAnalyticsLayout,
  setSettings,
  clearSettings,
} = settingsSlice.actions;

// Async thunks to persist settings to backend API. These are safe to call
// after updating local Redux state (optimistic) or standalone; on success
// we re-sync state with server response.
export const saveProfile = createAsyncThunk(
  "settings/saveProfile",
  async (profile, { rejectWithValue }) => {
    try {

      const url = `${API_BASE_URL}/settings/profile`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(profile || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      const errorText = await res.text();
      console.error("saveProfile failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      console.error("saveProfile error", e);
      return rejectWithValue(e.message);
    }
  },
);

export const saveCompany = createAsyncThunk(
  "settings/saveCompany",
  async (company, { rejectWithValue }) => {
    try {

      const res = await fetch(`${API_BASE_URL}/settings/company`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(company || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      const errorText = await res.text();
      console.error("saveCompany failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      console.error("saveCompany error", e);
      return rejectWithValue(e.message);
    }
  },
);

export const saveBank = createAsyncThunk(
  "settings/saveBank",
  async (bank, { rejectWithValue }) => {
    try {

      const res = await fetch(`${API_BASE_URL}/settings/bank`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(bank || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      const errorText = await res.text();
      console.error("saveBank failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      console.error("saveBank error", e);
      return rejectWithValue(e.message);
    }
  },
);

export const saveNotifications = createAsyncThunk(
  "settings/saveNotifications",
  async (notifications, { rejectWithValue }) => {
    try {

      const res = await fetch(`${API_BASE_URL}/settings/notifications`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(notifications || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      // eslint-disable-next-line no-console
      const errorText = await res.text();
      console.error("saveNotifications failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("saveNotifications error", e);
      return rejectWithValue(e.message);
    }
  },
);

export const saveColumnPreferences = createAsyncThunk(
  "settings/saveColumnPreferences",
  async (columnPreferences, { rejectWithValue }) => {
    try {

      const res = await fetch(`${API_BASE_URL}/settings/column-preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(columnPreferences || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      const errorText = await res.text();
      console.error("saveColumnPreferences failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      console.error("saveColumnPreferences error", e);
      return rejectWithValue(e.message);
    }
  },
);

export const saveAnalyticsLayout = createAsyncThunk(
  "settings/saveAnalyticsLayout",
  async (analyticsLayout, { rejectWithValue }) => {
    try {

      const res = await fetch(`${API_BASE_URL}/settings/analytics-layout`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(analyticsLayout || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
      const errorText = await res.text();
      console.error("saveAnalyticsLayout failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      console.error("saveAnalyticsLayout error", e);
      return rejectWithValue(e.message);
    }
  },
);

export const loadSettings = createAsyncThunk(
  "settings/loadSettings",
  async (_, { rejectWithValue }) => {
    try {


      // Fetch both settings and user data in parallel
      // user-scoped settings + auth, and global site-settings (public)
      const [settingsRes, userRes, siteRes] = await Promise.all([
        fetch(`${API_BASE_URL}/settings`, {
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/site-settings`),
      ]);

      const settingsOk = settingsRes.ok;
      const userOk = userRes.ok;
      const siteOk = siteRes.ok;

      const settingsData = settingsOk ? await settingsRes.json() : null;
      const userData = userOk ? await userRes.json() : null;
      const siteData = siteOk ? await siteRes.json() : null;

      // Merge user data (plan, planActivatedAt, etc.) into profile when available
      const result = settingsData || {
        profile: {},
        company: {},
        bank: {},
        notifications: {},
        columnPreferences: {},
        analyticsLayout: { order: [] },
      };

      if (userData && result) {
        result.profile = {
          ...result.profile,
          plan: userData.plan,
          planActivatedAt: userData.planActivatedAt,
          planExtraDays: userData.planExtraDays || 0,
          createdAt: userData.createdAt,
          subscription: userData.subscription,
        };
      }

      // Attach site-level settings if available (youtubeUrl etc.)
      if (siteData) {
        result.youtubeUrl = siteData.youtubeUrl || result.youtubeUrl;
        result.siteSettings = siteData;
      }

      return result;

      const errorText = await settingsRes.text();
      console.error("loadSettings failed", errorText);
      return rejectWithValue(errorText);
    } catch (e) {
      console.error("loadSettings error", e);
      return rejectWithValue(e.message);
    }
  },
);

export default settingsSlice.reducer;
